import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Top-bar bell button + real-time toast popup for incoming notifications.
 *
 * Polls /notifications/inbox every 25s. On the first poll we record the set
 * of known delivery IDs; on every subsequent poll, any new delivery surfaces
 * as a sonner toast that links to /inbox AND increments the red badge dot.
 *
 * This is effectively "web push" via polling — every signed-in user sees the
 * notification within ~25s of the admin clicking Send, even on the web app
 * where native FCM/APNs isn't wired up.
 */
export default function InboxBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const seenRef = useRef(null); // Set<delivery_id> — null means "first load"
  const eligible =
    user &&
    (user.role === "admin" ||
      user.role === "agency_user" ||
      user.role === "distributor");

  useEffect(() => {
    if (!eligible) {
      setUnread(0);
      seenRef.current = null;
      return undefined;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get("/notifications/inbox", {
          params: { limit: 50 },
        });
        if (cancelled) return;
        const items = Array.isArray(data) ? data : [];
        const unreadCount = items.filter((d) => !d.read_at).length;
        setUnread(unreadCount);

        if (seenRef.current === null) {
          // First poll — just snapshot what's there; don't toast on initial load.
          seenRef.current = new Set(items.map((d) => d.delivery_id));
          return;
        }
        // Any delivery_id we haven't seen before is brand new → toast it.
        const fresh = items.filter(
          (d) => !seenRef.current.has(d.delivery_id)
        );
        for (const d of fresh) {
          seenRef.current.add(d.delivery_id);
          toast(d.title, {
            description: d.body,
            duration: 8000,
            action: {
              label: "Open",
              onClick: () => navigate("/inbox"),
            },
          });
        }
      } catch {
        /* network blip — try again next tick */
      }
    };

    poll();
    const id = setInterval(poll, 25_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [eligible, navigate]);

  if (!eligible) return null;

  return (
    <button
      type="button"
      onClick={() => navigate("/inbox")}
      className="relative w-9 h-9 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-900 flex items-center justify-center transition-colors"
      aria-label={`Open inbox${unread ? ` (${unread} unread)` : ""}`}
      data-testid="topbar-inbox-bell"
    >
      <Bell className="w-4 h-4" />
      {unread > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
          data-testid="topbar-inbox-badge"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </button>
  );
}
